import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentService } from '@evorait/pace-environment';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { HttpClient } from '@angular/common/http';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    catchError,
    combineLatest,
    filter,
    from,
    fromEvent,
    map,
    mergeMap,
    Observable,
    of,
    retry,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { ConfigConstants } from '../../../config/config-constants';
import { ModalClass } from '../../../enums/modal-bootstrap-classes.enum';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../../interfaces/synchronization.interface';
import { isDefined } from '../../../utilities/is-defined';
import { AppNotificationService } from '../../../services/app-notification/app-notification.service';
import { AppNotificationType } from '../../../services/app-notification/app-notification.model';
import { SubscribePushNotificationVisualGuideComponent } from '../../../components/subscribe-push-notifications-visual-guide/subscribe-push-notifications-visual-guide.component';
import {
    AdditionalDataPushNotificationUpdate,
    isPushNotificationUpdateForNotification,
    PushNotificationUpdateForNotification,
} from '../../../services/pwa-push-notification/pwa-push-notification-stored-data.type';
import { PwaPushNotificationsRequest } from '../../../services/pwa-push-notification/pwa-push-notifications-request.type';
import {
    loadPwaPushNotificationSettings,
    loadPwaPushNotificationSettingsError,
    loadPwaPushNotificationSettingsSuccess,
    offerPushNotificationSubscription,
    pushSubscriptionHealthy,
    pushSubscriptionInactive,
    reconcilePushSubscription,
    savePwaPushNotificationSettings,
    subscribeToPushNotifications,
    subscribeToPushNotificationsFailure,
    subscribeToPushNotificationsSuccess,
} from '../actions/pwa-push-notification.actions';
import { LoadStatus } from '../../load-status';
import { selectBackendName } from '../../config';
import { selectSelectedUser } from '../../user-master';
import { PwaPushNotificationsLocalStorageService } from '../../../services/pwa-push-notification/pwa-push-notifications.local-storage.service';
import {
    selectIsUserSubscribedToPushNotifications,
    selectPwaPushNotificationSettings,
    selectPwaPushNotificationSettled,
    selectPwaPushNotificationStatus,
} from '../selectors/pwa-push-notification.selector';

@Injectable()
export class PwaPushNotificationEffects {
    private static readonly maxRetries = 3;
    private static readonly retryDelay = 1000;

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly swPush: SwPush,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly environmentService: EnvironmentService,
        private readonly offlineService: OfflineService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translateService: TranslateService,
        private readonly modalService: BsModalService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(SynchronizationInstanceToken) private readonly synchronizationService: SynchronizationInterface,
        private readonly pwaPushNotificationsLocalStorageService: PwaPushNotificationsLocalStorageService,
    ) {}

    /**
     * Reads settings from device-local storage directly, rather than through the generic
     * `PERSISTED_SLICES`/`ConcurrentUsageService` mechanism other slices use — that store is
     * explicitly cross-device (keyed per gid, synced via the device-lock flow's
     * `downloadAll()`), which is wrong for a push subscription: it's inherently per-device, and
     * a forced device-lock takeover on another device previously clobbered this slice's local
     * "already subscribed" state back to nothing on next lock (re)acquisition.
     */
    public loadPwaPushNotificationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadPwaPushNotificationSettings),
            concatLatestFrom(() => this.store.select(selectPwaPushNotificationStatus)),
            filter(([, status]) => status === LoadStatus.Idle),
            switchMap(() =>
                from(this.pwaPushNotificationsLocalStorageService.getPwaPushNotificationsUserSettingsAsync()).pipe(
                    map((settings) => loadPwaPushNotificationSettingsSuccess({ settings })),
                    catchError((error) => {
                        this.log.error(
                            `PwaPushNotificationEffects: loadPwaPushNotificationSettings: failed to read device-local settings: ${JSON.stringify(error)}`,
                        );
                        return of(loadPwaPushNotificationSettingsError());
                    }),
                ),
            ),
        ),
    );

    public savePwaPushNotificationSettings$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(savePwaPushNotificationSettings),
                switchMap(({ settings }) =>
                    from(
                        this.pwaPushNotificationsLocalStorageService.savePwaPushNotificationsUserSettingsAsync(
                            settings,
                        ),
                    ).pipe(
                        catchError((error) => {
                            this.log.error(
                                `PwaPushNotificationEffects: savePwaPushNotificationSettings: failed to persist device-local settings: ${JSON.stringify(error)}`,
                            );
                            return of(undefined);
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public subscribeToPushNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(subscribeToPushNotifications),
            switchMap(() => {
                const serverPublicKey = this.environmentService.subscriptionServerPublicKey;
                const hostUrl = this.environmentService.apiBase;

                if (!serverPublicKey) {
                    return of(subscribeToPushNotificationsFailure());
                }

                return this.checkForNotificationPermissions().pipe(
                    switchMap((permissionState) => {
                        this.log.debug(
                            `PwaPushNotificationEffects: subscribeToPushNotifications: Permissions state is '${permissionState}'`,
                        );

                        if (permissionState !== ConfigConstants.PushNotificationsPermissionState.Granted) {
                            return of(subscribeToPushNotificationsFailure());
                        }

                        return from(this.swPush.requestSubscription({ serverPublicKey })).pipe(
                            mergeMap((subscription) => this.sendSubscriptionToServer(subscription, hostUrl)),
                            retry({
                                count: PwaPushNotificationEffects.maxRetries,
                                delay: PwaPushNotificationEffects.retryDelay,
                            }),
                            map(() => subscribeToPushNotificationsSuccess()),
                            catchError((error) => {
                                this.log.error(
                                    `PwaPushNotificationEffects: subscribeToPushNotifications: Error after ${PwaPushNotificationEffects.maxRetries} retries. Error: ${JSON.stringify(error)}`,
                                );
                                return of(subscribeToPushNotificationsFailure());
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    public saveSettingsOnSubscribeSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(subscribeToPushNotificationsSuccess),
            map(() =>
                savePwaPushNotificationSettings({
                    settings: { isUserSubscribed: true, hasUserBeenOfferedToSubscribe: true },
                }),
            ),
        ),
    );

    public dispatchReconcileOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadPwaPushNotificationSettingsSuccess),
            map(() => reconcilePushSubscription()),
        ),
    );

    public dispatchReconcileOnResume$ = createEffect(() =>
        fromEvent(document, 'visibilitychange').pipe(
            filter(() => document.visibilityState === 'visible'),
            map(() => reconcilePushSubscription()),
        ),
    );

    public dispatchReconcileOnBrowserChange$ = createEffect(() =>
        this.swPush.pushSubscriptionChanges.pipe(map(() => reconcilePushSubscription())),
    );

    public reconcilePushSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(reconcilePushSubscription),
            concatLatestFrom(() => this.store.select(selectPwaPushNotificationSettings)),
            filter(([, settings]) => settings?.isUserSubscribed === true),
            switchMap(() => {
                if (!('Notification' in window) || Notification.permission !== 'granted' || !this.swPush.isEnabled) {
                    return of(pushSubscriptionInactive());
                }

                return this.swPush.subscription.pipe(
                    take(1),
                    map((subscription) => (subscription ? pushSubscriptionHealthy() : subscribeToPushNotifications())),
                );
            }),
        ),
    );

    public saveSettingsOnSubscriptionInactive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pushSubscriptionInactive),
            map(() =>
                savePwaPushNotificationSettings({
                    settings: { isUserSubscribed: false, hasUserBeenOfferedToSubscribe: true },
                }),
            ),
        ),
    );

    /** Emits once the user is confirmed subscribed — either at init or after a fresh in-session subscription. */
    private readonly whenSubscriptionActive$ = this.actions$.pipe(
        ofType(loadPwaPushNotificationSettingsSuccess, subscribeToPushNotificationsSuccess),
        concatLatestFrom(() => this.store.select(selectIsUserSubscribedToPushNotifications)),
        filter(([action, isSubscribed]) => action.type === subscribeToPushNotificationsSuccess.type || isSubscribed),
    );

    public handleNotificationClicks$ = createEffect(
        () =>
            this.whenSubscriptionActive$.pipe(
                switchMap(() =>
                    this.swPush.notificationClicks.pipe(
                        filter(isPushNotificationUpdateForNotification),
                        switchMap((pushUpdateForNotification) =>
                            of(pushUpdateForNotification).pipe(
                                mergeMap(() => this.offlineService.isSyncing$),
                                filter((isSyncInProgress) => !isSyncInProgress),
                                mergeMap(() =>
                                    this.router.navigate([
                                        ConfigConstants.routeUrlDetails,
                                        pushUpdateForNotification.notification.data.gid,
                                        pushUpdateForNotification.notification.data.notificationNumber,
                                        pushUpdateForNotification.notification.data.taskNumber,
                                    ]),
                                ),
                            ),
                        ),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public handleMessages$ = createEffect(
        () =>
            this.whenSubscriptionActive$.pipe(
                switchMap(() =>
                    this.swPush.messages.pipe(
                        mergeMap((message) =>
                            combineLatest([
                                this.synchronizationService.receiveNotifications(),
                                this.synchronizationService.receiveWhatsNew().pipe(
                                    tap(() => {
                                        if (
                                            this.isAdditionalDataPresent(
                                                message as
                                                    | PushNotificationUpdateForNotification
                                                    | AdditionalDataPushNotificationUpdate,
                                            )
                                        ) {
                                            const casted = message as AdditionalDataPushNotificationUpdate;
                                            const additionalData = casted.additionalData?.data;

                                            if (isDefined(additionalData)) {
                                                this.appNotificationService.setAppNotification(
                                                    AppNotificationType.INFO,
                                                    <string>this.translateService.instant('PUSH_NOTIFICATION_TITLE'),
                                                    `${additionalData}`,
                                                );
                                            }
                                        }
                                    }),
                                    map(() => message),
                                    catchError((error) => {
                                        this.log.error(
                                            `PwaPushNotificationEffects: handleMessages: error processing message: ${JSON.stringify(error)}`,
                                        );
                                        return throwError(() => error);
                                    }),
                                ),
                            ]),
                        ),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public offerPushNotificationSubscription$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(offerPushNotificationSubscription),
                switchMap(() =>
                    this.store.select(selectPwaPushNotificationSettled).pipe(
                        filter((settled) => settled),
                        take(1),
                    ),
                ),
                switchMap(() => this.store.select(selectPwaPushNotificationSettings)),
                filter(
                    (settings) =>
                        (!isDefined(settings) ||
                            (!settings.isUserSubscribed && !settings.hasUserBeenOfferedToSubscribe)) &&
                        window.matchMedia('(display-mode: standalone)').matches,
                ),
                tap(() =>
                    this.modalService.show(SubscribePushNotificationVisualGuideComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        ariaLabelledBy: 'subscribe-push-notification-modal-title',
                    }),
                ),
            ),
        { dispatch: false },
    );

    private checkForNotificationPermissions(): Observable<ConfigConstants.PushNotificationsPermissionState> {
        if ('Notification' in window) {
            return from(Notification.requestPermission()).pipe(
                map((permission) => {
                    switch (permission) {
                        case 'granted':
                            this.log.warn('PwaPushNotificationEffects: Notifications: User allowed notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Granted;
                        case 'denied':
                            this.log.warn('PwaPushNotificationEffects: Notifications: User blocked notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Denied;
                        default:
                            this.log.warn('PwaPushNotificationEffects: Notifications: User dismissed notifications');
                            return ConfigConstants.PushNotificationsPermissionState.Default;
                    }
                }),
            );
        } else {
            this.log.warn('PwaPushNotificationEffects: This browser does not support notifications');
            return of(ConfigConstants.PushNotificationsPermissionState.Denied);
        }
    }

    private sendSubscriptionToServer(subscription: PushSubscription, hostUrl: string): Observable<unknown> {
        const userModel = this.store.selectSignal(selectSelectedUser)();

        const body: PwaPushNotificationsRequest = {
            gid: userModel?.frontId ?? '',
            salesOrg: userModel?.salesOrg,
            plant: userModel?.plant,
            country: userModel?.country,
            subscription: subscription,
            env: this.store.selectSignal(selectBackendName)(),
        };
        return this.http.post(`${hostUrl}${ENDPOINTS.WebPushSubscribe}`, JSON.stringify(body), {
            responseType: 'text',
        });
    }

    private isAdditionalDataPresent(
        message: PushNotificationUpdateForNotification | AdditionalDataPushNotificationUpdate,
    ): message is AdditionalDataPushNotificationUpdate {
        return 'additionalData' in message && typeof message.additionalData?.data === 'string';
    }
}
